<template>
  <a-modal
    :visible="visible"
    title="Autenticación en 2 pasos (2FA)"
    width="640px"
    @cancel="handleCancel"
    centered
  >
    <a-tabs v-if="isEdit" v-model:activeKey="currentStep">
      <a-tab-pane :key="0" tab="Roles" />
      <a-tab-pane :key="1" tab="Tiempo de expiración" />
    </a-tabs>

    <a-steps :style="{ padding: '10px 50px 0 50px' }" v-else progress-dot :current="currentStep" size="small" class="mb-4">
      <a-step title="Seleccionar roles" />
      <a-step title="Tiempo de expiración" />
    </a-steps>   

    <div :style="{ padding: '10px 50px' }">
      <div v-if="currentStep === 0">
        <div class="modal_title">
          <h2>Seleccionar roles</h2>
          <p class="text-secondary mb-4">
            Indica a qué roles aplicará la autenticación en 2 pasos (2FA).
          </p>
        </div>

        <p class="modal_subtitle">
          Selecciona los roles
          <span :style="{ color: '#00000073', fontWeight: 400 }">
            (Mínimo 1 rol)
          </span>
        </p>
        <a-checkbox-group v-model:value="formState.selectedRoles" :style="{ width: '100%' }">
          <div class="options_roles" v-for="role in roles" :key="role.value">
            <a-card class="option_role">
              <a-checkbox :value="role.value">
                {{ role.label }}
              </a-checkbox>
            </a-card>
          </div>
        </a-checkbox-group>
      </div>

      <div v-else>
        <div class="modal_title">
          <h2>Tiempo de expiración del código de verificación</h2>
          <p class="text-secondary mb-4">
            Indica el tiempo de duración que tendrá el código de verificación que se genere. 
            Se puede ingresar como máximo 72 horas.
          </p>
        </div>

        <a-card class="expiration_inputs">
          <a-form layout="inline">
            El código expirará en: &nbsp;&nbsp;
            <a-form-item>
              <a-input-number v-model:value="formState.expirationHours" :min="0" :max="72" :precision="0" />
            </a-form-item>
            horas y &nbsp;&nbsp;
            <a-form-item>
              <a-input-number v-model:value="formState.expirationMinutes" :min="0" :max="formState.expirationHours < 72 ? 59 : 0" :precision="0" />
            </a-form-item> 
            minutos
          </a-form>
        </a-card>
      </div>
    </div>

    <template #footer>
      <a-space class="w-full" direction="horizontal" style="justify-content: flex-end">
        <a-button v-if="currentStep === 0" @click="handleCancel">Cancelar</a-button>
        <a-button v-else @click="currentStep = 0">Volver</a-button>

        <a-tooltip
          v-if="currentStep === 0 && formState.selectedRoles.length === 0"
          title="Selecciona mínimo 1 rol para poder continuar."
        >
          <a-button type="primary" disabled>Continuar</a-button>
        </a-tooltip>

        <a-button
          v-else-if="currentStep === 0"
          type="primary"
          @click="currentStep++"
        >
          Continuar
        </a-button>

        <a-button
          v-else
          :loading="loading"
          type="primary"
          @click="handleFinish"
          :disabled="formState.expirationHours === 0 && formState.expirationMinutes === 0"
        >
          Confirmar
        </a-button>
      </a-space>
    </template>
  </a-modal>
</template>

<script lang="ts" setup>
import { MfaType } from '@/app/auth/utils/enums';
import { mfaConfigService } from '@/app/settingsMenu/services/MfaConfigService';
import { RolTypes } from '@/app/shared/utils/enums';
import { ResponseError } from '@/app/shared/utils/httpManager';
import { QuestionCircleOutlined } from '@ant-design/icons-vue';
import { message, Modal } from 'ant-design-vue';
import { createVNode, ref, watch } from 'vue'

const props = defineProps<{
  visible: boolean,
  mfaType: MfaType | null,
  isEdit: boolean,
  config: any,
}>()

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'next', payload: { roles: string[], hours: number, minutes: number }): void
  (e: 'enableConfig'): void
  (e: 'refresh'): void
}>()

interface RoleOption {
  label: string
  value: number
}

const currentStep = ref(0)
const formState = ref({
  selectedRoles: props.isEdit ? props.config.role : [RolTypes.owner],
  expirationHours: props.isEdit ? props.config.hours : 0,
  expirationMinutes: props.isEdit ? props.config.minutes : 0
});

watch(
  () => props.isEdit,
  (value: boolean) => {
    if (value) {
      formState.value = {
        selectedRoles: props.config.role ?? [RolTypes.owner],
        expirationHours: props.config.hours ?? 0,
        expirationMinutes: props.config.minutes ?? 0
      }
    }
  }
)

// const selectedRoles = ref<number[]>([RolTypes.owner])
// const expirationHours = ref(0)
// const expirationMinutes = ref(0)
const loading = ref(false);

const roles: RoleOption[] = [
  { label: 'Owner', value: RolTypes.owner },
  { label: 'Administradores', value: RolTypes.administrator },
  { label: 'Agentes', value: RolTypes.agent }
]

function handleCancel() {
  emit('update:visible', false)
  currentStep.value = 0
}

const handleFinish = async () => {
  Modal.confirm({
    centered: true,
    icon: createVNode(QuestionCircleOutlined),
    title: "¿Estás seguro de guardar tus cambios?",
    content: "Se guardara la configuración de autenticación en 2 pasos",
    okText: 'Aceptar',
    onOk: async () => {
      loading.value = true;

      try {
        await mfaConfigService.enableMfa({
          role: formState.value.selectedRoles,
          // hours: expirationHours.value,
          // minutes: expirationMinutes.value,
          time_to_expire: (formState.value.expirationHours * 3600) + (formState.value.expirationMinutes * 60),
          mfa_type: props.mfaType,
        });
        emit('update:visible', false)
        emit('enableConfig')
        emit('refresh')
        currentStep.value = 0
        message.success("Se guardó la configuración correctamente")
      } catch (error) {
        const messageError =
          error instanceof ResponseError ? error.getErrorMessage() : "Ha ocurrido un error";

        message.error(messageError);
      }

      loading.value = false;
    },
  });
};
</script>

<style scoped lang="sass">
.text-secondary
  color: #888

.mb-4
  margin-bottom: 1rem

.modal
  &_title
    width: 100%
    text-align: center
  &_subtitle
    font-weight: 600

.options_roles
  width: 100%
  .option_role
    width: 100%
    margin-bottom: 12px
    :deep(.ant-card-body)
      padding: 12px
      border-radius: 0 0 10px 10px

.expiration_inputs
  display: flex
  justify-content: center
  margin-top: 24px
  margin-bottom: 100px
  :deep(.ant-card-body)
    padding: 12px

  form
    align-items: center
</style>
